import React from "react";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import SunIcon from "./Images/sun.svg";
import EditableText from "./EditableText";
import { r, s, toneFor } from "./reportTokens";

/**
 * The marker writes "... is correct - 1 mark. ..." and the design sets the mark
 * itself in medium. The stored feedback is plain text, so the weight is applied
 * by matching the mark phrase rather than by storing markup.
 */
const MARK_PHRASE = /(\b\d+\s+marks?\b)/gi;
// Separate, non-global copy: a /g regex keeps lastIndex between .test() calls,
// which would emphasise only every other match.
const IS_MARK_PHRASE = /^\d+\s+marks?$/i;

const withMarksEmphasised = (text) =>
  text.split(MARK_PHRASE).map((part, index) =>
    IS_MARK_PHRASE.test(part) ? (
      <Text as="span" key={index} fontWeight="500">
        {part}
      </Text>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  );

/** The AI marker joins its two sections with blank lines; keep them as paragraphs. */
const paragraphsOf = (text) =>
  (text || "").split(/\n\s*\n/).map((block) => block.trim()).filter(Boolean);

/** Shared by the status and topic pills: page tint, 0.25pt hairline (96:208). */
const outlinedPill = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: r.outlineBg,
  border: `${r.hairline} solid ${r.outlineBorder}`,
};

/** Floating status pill above the card (Figma 96:252). */
const StatusPill = ({ tone, scored, total }) => (
  <Flex {...outlinedPill} gap={s(10)} borderRadius={s(46)} padding={`${s(4)} ${s(10)}`}>
    <Image src={tone.dot} alt="" width={s(13)} height={s(13)} />
    <Text fontSize={s(12)} fontWeight="400" color={r.ink} whiteSpace="nowrap">
      {tone.label}{" "}
      <Text as="span" fontWeight="500">
        {scored}/{total}
      </Text>
    </Text>
  </Flex>
);

/**
 * The timeline down the left of the card: a dot at the answer, a hairline, and
 * a dot at the end of the feedback. It stretches with the content rather than
 * sitting at the design's fixed 95px, because real feedback varies in length.
 */
const Rail = ({ tone }) => (
  <Flex direction="column" alignItems="center" flexShrink={0} width={s(6)} alignSelf="stretch">
    <Image src={tone.rail} alt="" width={s(6)} height={s(6)} />
    <Box flex="1" width="1px" bg="rgba(0,0,0,0.55)" marginY={s(3)} />
    <Image src={tone.rail} alt="" width={s(6)} height={s(6)} />
  </Flex>
);

/**
 * Move whitespace out of a highlighted segment and into its neighbours.
 * The highlight is a padded block, so a segment that starts or ends with a
 * space paints that space yellow and opens a gap against the word beside it.
 */
const tidyHighlights = (segments) => {
  const out = [];
  const pushPlain = (text) => {
    if (!text) return;
    const last = out[out.length - 1];
    if (last && !last.added) last.text += text;
    else out.push({ text, added: false });
  };
  (segments || []).forEach((segment) => {
    if (!segment.added) {
      pushPlain(segment.text || "");
      return;
    }
    const [, lead, core, trail] = /^(\s*)([\s\S]*?)(\s*)$/.exec(segment.text || "");
    pushPlain(lead);
    if (core) out.push({ text: core, added: true });
    pushPlain(trail);
  });
  return out;
};

/**
 * The student's answer rebuilt to full marks. The backend returns it already
 * split into segments tagged added true/false, so the marker stroke goes under
 * exactly the parts that were added rather than under a guess from diffing.
 */
const ModelAnswer = ({ segments, editing, draftText, onDraftText }) => (
  <Box
    className="report-block"
    bg={r.modelBg}
    borderRadius={s(14)}
    minHeight={s(72)}
    marginLeft={s(28)}
    marginRight={s(43)}
    marginTop={s(14)}
    padding={`${s(5)} ${s(10)} ${s(14)}`}
  >
    <Flex alignItems="center" gap={s(5)}>
      <Image src={SunIcon} alt="" width={s(24)} height={s(24)} />
      <Text fontSize={s(11)} fontWeight="500" color={r.ink}>
        Model Answer
      </Text>
    </Flex>
    {editing ? (
      <Box paddingLeft={s(7)} marginTop={s(10)}>
        <EditableText
          value={draftText}
          onChange={onDraftText}
          placeholder="Write the model answer, or leave this empty to drop it from the report."
          minHeight={s(60)}
        />
        <Text className="report-edit-only" fontSize={s(9)} color={r.muted} marginTop={s(4)}>
          Wrap anything you want highlighted in ==double equals==. Clearing the
          box removes the model answer from the report.
        </Text>
      </Box>
    ) : (
    <Text fontSize={s(11)} color="#000000" lineHeight="1.55" paddingLeft={s(7)} marginTop={s(10)}>
      {tidyHighlights(segments).map((segment, index) =>
        segment.added ? (
          <Text
            as="span"
            key={index}
            bg={r.highlight}
            color="#000000"
            padding={`${s(2)} ${s(4)}`}
            // Each wrapped line gets its own block rather than one box drawn
            // around the whole run.
            sx={{ WebkitBoxDecorationBreak: "clone", boxDecorationBreak: "clone" }}
          >
            {segment.text}
          </Text>
        ) : (
          <React.Fragment key={index}>{segment.text}</React.Fragment>
        )
      )}
    </Text>
    )}
  </Box>
);

const ReportQuestionCard = ({ question, editing = false, draft, onDraft }) => {
  const total = question.total_mark || 0;
  const scored = question.scored_mark ?? 0;
  const tone = toneFor(question.correctness, scored, total);
  const answer = (question.student_answer || "").trim();
  const answerImages = question.answer_images || [];
  const feedbackParagraphs = paragraphsOf(question.feedback);
  const modelAnswer = question.model_answer || [];
  const lostMarks = total > 0 && scored < total;

  const why = editing ? draft?.why ?? question.why ?? "" : question.why;
  const modelAnswerText = draft?.model_answer_text ?? question.model_answer_text ?? "";
  const setField = (field) => (value) => onDraft?.({ ...draft, [field]: value });

  // Editing offers both blocks on any question that dropped marks, even where
  // the generator declined to write one: the teacher can add what it would not.
  const showWhy = editing ? lostMarks : Boolean(question.why);
  const showModelAnswer = editing ? lostMarks : modelAnswer.length > 0;

  return (
    <Box marginTop={s(30)} className="report-question">
      {/* The pill sits inside the card's top right corner (Figma 99:58), so the
          card's top padding leaves room for it. */}
      <Box
        position="relative"
        bg={r.card}
        borderRadius={s(14)}
        padding={`${s(45)} ${s(24)} ${s(20)}`}
      >
        <Box position="absolute" top={s(6)} right={s(6)}>
          <StatusPill tone={tone} scored={scored} total={total} />
        </Box>
        <Flex className="report-heading" alignItems="flex-start" gap={s(6)}>
          <Text fontSize={s(12)} fontWeight="500" color={r.ink} flexShrink={0}>
            {question.label}
          </Text>
          <Text fontSize={s(12)} fontWeight="500" color={r.inkSoft} lineHeight="1.35">
            {question.question_text}
          </Text>
        </Flex>

        {/* rail + answer + feedback */}
        <Flex marginTop={s(14)} paddingLeft={s(7)} gap={s(15)} alignItems="stretch">
          <Rail tone={tone} />

          <Box flex="1" minWidth={0}>
            <Text fontSize={s(11)} color={r.ink} paddingLeft={s(6)} lineHeight="1.4">
              <Text as="span" fontWeight="500">
                My Answer :{" "}
              </Text>
              {answer || (answerImages.length > 0 ? "" : "Not answered.")}
            </Text>

            {/* Work handed in as a photo or a drawing. Shown whole: contain
                never crops, and the height cap stops a phone photo taking a
                page to itself. */}
            {answerImages.length > 0 && (
              <Flex
                gap={s(8)}
                marginTop={s(8)}
                paddingLeft={s(6)}
                marginRight={s(43)}
                wrap="wrap"
                className="report-answer-images"
              >
                {answerImages.map((url) => (
                  <Image
                    key={url}
                    src={url}
                    alt="The student's answer"
                    maxWidth="100%"
                    maxHeight={s(240)}
                    objectFit="contain"
                    borderRadius={s(14)}
                    border={`${r.hairline} solid ${r.outlineBorder}`}
                    display="block"
                  />
                ))}
              </Flex>
            )}

            <Box
              className="report-block"
              bg={tone.bg}
              borderRadius={s(14)}
              marginTop={s(10)}
              marginRight={s(43)}
              padding={`${s(15)} ${s(16)} ${s(18)}`}
            >
              <Flex alignItems="center" gap={s(6)}>
                <Image src={tone.dot} alt="" width={s(11)} height={s(11)} />
                <Text fontSize={s(11)} fontWeight="500" color={r.ink}>
                  Feedback
                </Text>
              </Flex>
              <Box paddingLeft={s(17)} marginTop={s(10)}>
                {feedbackParagraphs.length === 0 && (
                  <Text fontSize={s(11)} color={r.muted}>
                    No feedback was recorded for this question.
                  </Text>
                )}
                {feedbackParagraphs.map((paragraph, index) => (
                  <Text
                    key={index}
                    fontSize={s(11)}
                    color="#000000"
                    lineHeight="1.45"
                    marginTop={index === 0 ? 0 : s(8)}
                  >
                    {withMarksEmphasised(paragraph)}
                  </Text>
                ))}
              </Box>
            </Box>
          </Box>
        </Flex>

        {/* Only where marks were lost: the design's Correct card has no such
            block, and the backend only writes a note for those questions. */}
        {showWhy && (
          <Box className="report-block" paddingLeft={s(34)} marginTop={s(18)} marginRight={s(43)}>
            <Text className="report-heading" fontSize={s(11)} fontWeight="500" color={r.ink}>
              What went wrong &amp; why
            </Text>
            {editing ? (
              <Box marginTop={s(8)}>
                <EditableText
                  value={why}
                  onChange={setField("why")}
                  placeholder="What went wrong on this question, and why."
                />
              </Box>
            ) : (
              <Text fontSize={s(11)} color={r.ink} marginTop={s(8)} lineHeight="1.45" maxWidth={s(408)}>
                {why}
              </Text>
            )}
          </Box>
        )}

        {showModelAnswer && (
          <ModelAnswer
            segments={modelAnswer}
            editing={editing}
            draftText={modelAnswerText}
            onDraftText={setField("model_answer_text")}
          />
        )}

        {question.topic && (
          <Flex alignItems="center" gap={s(7)} marginTop={s(20)} paddingLeft={s(9)}>
            <Text fontSize={s(11)} fontWeight="500" color="#000000" whiteSpace="nowrap">
              Question Topic:
            </Text>
            <Flex {...outlinedPill} height={s(21)} borderRadius={s(22)} padding={`0 ${s(10)}`}>
              <Text fontSize={s(9)} fontWeight="400" color={r.ink} whiteSpace="nowrap">
                {question.topic}
              </Text>
            </Flex>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default ReportQuestionCard;
